import { useMemo } from 'react';
import type { CohortScope, DateRange, Filters, SeriesSet } from '../types/dashboard';
import { useFilters } from '../contexts/FilterContext';
import { makeCohortScope, makeScope, resolveTerritoryIds } from '../utils/filters';
import { resolveRange, shiftRange } from '../utils/calendar';
import { computeGrouped, computeSeries } from '../utils/metrics';
import { ALL_TERRITORY_IDS, activeTerritoryCount, territoryById } from '../utils/territory';

export interface DashboardData {
  filters: Filters;
  range: DateRange;
  lyRange: DateRange | null;
  territoryIds: string[];
  isNetworkScope: boolean;
  singleTerritory: boolean;
  scopeLabel: string;
  cohortScope: CohortScope;
  networkCohortScope: CohortScope;
  series: SeriesSet;
  network: SeriesSet;
  grouped: Map<string, SeriesSet>;
  scopeUnits: (m: number) => number;
  networkUnits: (m: number) => number;
}

export function useDashboardData(): DashboardData {
  const { filters } = useFilters();
  return useMemo(() => {
    const range = resolveRange(filters);
    const territoryIds = resolveTerritoryIds(filters);
    const aopIds = filters.aops.length ? filters.aops : null;
    const scope = makeScope(filters, territoryIds, aopIds);
    const isNetworkScope = territoryIds.length === ALL_TERRITORY_IDS.length && !aopIds;
    const singleTerritory = territoryIds.length === 1 && !aopIds;
    return {
      filters,
      range,
      lyRange: shiftRange(range, -12),
      territoryIds,
      isNetworkScope,
      singleTerritory,
      scopeLabel: isNetworkScope ? 'Network' : singleTerritory ? territoryById.get(territoryIds[0])?.name ?? 'Territory' : 'Selected scope',
      cohortScope: makeCohortScope(filters, territoryIds, aopIds),
      networkCohortScope: makeCohortScope(filters, ALL_TERRITORY_IDS, null),
      series: computeSeries(scope),
      network: computeSeries(makeScope(filters, ALL_TERRITORY_IDS, null)),
      grouped: computeGrouped(scope, (r) => r.t),
      scopeUnits: (m: number) => activeTerritoryCount(territoryIds, m),
      networkUnits: (m: number) => activeTerritoryCount(ALL_TERRITORY_IDS, m),
    };
  }, [filters]);
}
